import React, { useEffect, useRef, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext } from "@mui/lab";
import { IconButton, TextField } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";

import "./Section.css";
import SuccessAlert from "../components/success-alert/SuccessAlert";
import ErrorAlert from "../components/error-alert/ErrorAlert";
import useLanguages from "../hooks/useLanguages";
import { API, SERVER_DOMAIN } from "../assets/staticData";
import { toast } from "react-toastify";
import { CACHE_KEY } from "../constant/cache-key";
import ClearCacheButton from "../components/ClearCacheButton/ClearCacheButton";

export default function DandW({ user }) {
  const langs = useLanguages();

  const [value, setValue] = useState("0");
  const [dataTable, setDataTable] = useState([{}]);
  const [rows, setRows] = useState([{}]);
  const [sections, setSections] = useState([{}]);
  const [data, setData] = useState([]);
  const [success, setSuccess] = useState(false);
  const [fail, setFail] = useState(false);
  const [loading, setLoading] = useState(true);

  const fileInputRefs = useRef([]);

  const handleFileInputClick = (index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click();
    }
  };

  let userToken = localStorage.getItem("userToken");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let tabStyle = { textTransform: "capitalize", fontWeight: "500" };

  // Get Data from backend
  useEffect(() => {
    async function getData() {
      await fetch(`${API}/dw`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      }).then(async (res) => {
        const response = await res.json();
        const apiData = response.data?.attributes?.data || [];

        if (apiData[0]?.header) {
          setDataTable(apiData);
        } else {
          let tableDocs = langs.map((item) => ({
            header: [...Array(5)].map(() => {
              return { icon: "", title: "", btnT: "", btnL: "" };
            }),

            sections: [
              {
                title: "",
                desc: "",
                rows: [
                  {
                    n: "",
                    v1: "",
                    v2: "",
                    v3: "",
                    v4: "",
                    v5: "",
                  },
                ],
              },
            ],
            language: item.attributes.language,
          }));
          setDataTable(tableDocs);
        }

        setLoading(false);
      });
    }

    getData();
  }, [langs]);

  const submitData = () => {
    fetch(`${API}/dw`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        data: { data: dataTable },
      }),
    }).then(async (res) => {
      if (res.ok) {
        setSuccess(true);
      } else {
        setFail(true);
      }
    });
  };

  const addSection = (index) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((item, i) => {
        if (i === index) {
          const columnCount = Object.keys(item.columns || {}).length;

          const newRows = Array.from({ length: columnCount }, () =>
            Array(columnCount)
              .fill("")
              .reduce((acc, _, colIndex) => ({ ...acc, [`v${colIndex + 1}`]: "" }), { n: "" })
          );

          const newSection = {
            title: "",
            desc: "",
            rows: newRows,
          };

          return { ...item, sections: [...item.sections, newSection] };
        }
        return item;
      })
    );
  };

  const removeSection = (tableIndex, sectionIndex) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((item, i) =>
        i === tableIndex
          ? {
            ...item,
            sections: item.sections.filter((_, sIndex) => sIndex !== sectionIndex),
          }
          : item
      )
    );
  };

  const addRow = (tableIndex, sectionIndex) => {
    const newRow = { n: "", v1: "", v2: "", v3: "", v4: "", v5: "" };

    setDataTable((prevDataTable) =>
      prevDataTable.map((item, i) =>
        i === tableIndex
          ? {
            ...item,
            sections: item.sections.map((section, sIndex) =>
              sIndex === sectionIndex ? { ...section, rows: [...section.rows, newRow] } : section
            ),
          }
          : item
      )
    );
  };

  const removeRow = (tableIndex, sectionIndex, rowIndex) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((item, i) =>
        i === tableIndex
          ? {
            ...item,
            sections: item.sections.map((section, sIndex) =>
              sIndex === sectionIndex
                ? {
                  ...section,
                  rows: section.rows.filter((_, rIndex) => rIndex !== rowIndex),
                }
                : section
            ),
          }
          : item
      )
    );
  };

  const addColumn = (tableIndex) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((table, i) =>
        i === tableIndex
          ? {
            ...table,
            header: [...table.header, { title: `Column ${table.header.length + 1}` }],
            sections: table.sections.map((section) => ({
              ...section,
              rows: section.rows.map((row) => ({
                ...row,
                [`v${table.header.length + 1}`]: "",
              })),
            })),
          }
          : table
      )
    );
  };

  const deleteColumn = (tableIndex, columnIndex) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((table, i) =>
        i === tableIndex
          ? {
            ...table,
            header: table.header.filter((_, colIndex) => colIndex !== columnIndex),
            sections: table.sections.map((section) => ({
              ...section,
              rows: section.rows.map((row) => {
                const updatedRow = { ...row };
                delete updatedRow[`v${columnIndex + 1}`];
                for (let col = columnIndex + 1; col <= 5; col++) {
                  updatedRow[`v${col}`] = updatedRow[`v${col + 1}`];
                  delete updatedRow[`v${col + 1}`];
                }
                return updatedRow;
              }),
            })),
          }
          : table
      )
    );
  };

  const handleInputChange = (tableIndex, headerIndex, value, type) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((table, i) =>
        i === tableIndex
          ? {
            ...table,
            header: table.header.map((header, j) =>
              j === headerIndex ? { ...header, [type]: value } : header
            ),
          }
          : table
      )
    );
  };

  const handleSectionInputChange = (tableIndex, sectionIndex, value, type) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((table, i) =>
        i === tableIndex
          ? {
            ...table,
            sections: table.sections.map((section, j) =>
              j === sectionIndex ? { ...section, [type]: value } : section
            ),
          }
          : table
      )
    );
  };

  const handleRowInputChange = (tableIndex, sectionIndex, rowIndex, value, type) => {
    setDataTable((prevDataTable) =>
      prevDataTable.map((table, i) =>
        i === tableIndex
          ? {
            ...table,
            sections: table.sections.map((section, j) =>
              j === sectionIndex
                ? {
                  ...section,
                  rows: section.rows.map((row, k) =>
                    k === rowIndex ? { ...row, [type]: value } : row
                  ),
                }
                : section
            ),
          }
          : table
      )
    );
  };

  const handleIconUpload = (index, headerIndex, e) => {
    const formData = new FormData();
    const file = e.target.files[0];

    if (file) {
      formData.append("files", file);

      // Upload the file
      fetch(`${API}/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      })
        .then(async (res) => {
          if (!res.ok) throw new Error("Upload failed");
          const response = await res.json();
          const iconURL = response[0]?.url;

          if (iconURL) {
            const updatedData = dataTable.map((item, idx) => {
              if (idx === index) {
                const updatedHeader = item.header.map((header, hIdx) => {
                  if (hIdx === headerIndex) {
                    return { ...header, icon: iconURL };
                  }
                  return header;
                });
                return { ...item, header: updatedHeader };
              }
              return item;
            });

            setDataTable(updatedData);

            return fetch(`${API}/dw`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userToken}`,
              },
              body: JSON.stringify({
                data: { data: updatedData },
              }),
            });
          }
        })
        .then((res) => {
          if (res && res.ok) {
            toast("Updated Successfully", {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
              type: "success",
            });
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          toast("Failed to Update", {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            type: "error",
          });
        });
    }
  };

  return (
    <TabContext value={value}>

      <Box sx={{ width: "100%" }}>
        <h3 style={{ textTransform: "capitalize" }}>Deposit & Withdraw Settings</h3>

        <Box mt={2}>
          <ClearCacheButton
            keys={[
              CACHE_KEY.depositAndWithdrawal,
              CACHE_KEY.startupConfigs
            ]} />
        </Box>

        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
          aria-label="secondary tabs">
          {langs?.map((item, index) => {
            return (
              <Tab
                key={index}
                sx={tabStyle}
                value={index.toString()}
                label={item.attributes.language}
                icon={
                  <img
                    src={SERVER_DOMAIN + item.attributes.icon.data.attributes.url}
                    style={{ width: 25, aspectRatio: 1, borderRadius: 20 }}
                  />
                }
                iconPosition="start"
              />
            );
          })}
        </Tabs>

        {loading ? (
          <></>
        ) : (
          <>
            {langs?.map((item, index) => {
              return (
                <TabPanel value={index.toString()} key={index}>
                  <SuccessAlert show={success} setShow={(v) => setSuccess(v)} />
                  <ErrorAlert show={fail} setShow={(v) => setFail(v)} />

                  <h3>Data Table</h3>

                  <div className=" border min-h-[300px] mt-3 pb-20 pr-10 relative border-gray-400 rounded-lg overflow-hidden transition-all delay-200">
                    <div
                      className="flex"
                      style={{
                        justifyContent: "space-around",
                        padding: "0 5px",
                      }}>
                      {dataTable[index]?.header.map((i, j) => (
                        <div
                          className="flex-col gap-5 border-l relative"
                          style={{
                            borderLeftWidth: j == 0 ? 0 : null,
                          }}
                          key={j}>
                          <IconButton
                            onClick={() => deleteColumn(index, j)}
                            style={{ position: "absolute", top: 0, right: 0 }}>
                            <ClearIcon sx={{ color: "#d70b0b" }} />
                          </IconButton>
                          <div
                            className="group flex relative w-[70px] mt-4 rounded-[15px] overflow-hidden cursor-pointer "
                            onClick={() => handleFileInputClick(j)}>
                            <img
                              style={{
                                width: "100%",
                                aspectRatio: 1,
                                objectFit: "cover",
                                transition: "0.2s",
                              }}
                              src={
                                i.icon
                                  ? SERVER_DOMAIN + i.icon
                                  : require("../assets/placeholder-image.png")
                              }
                            />

                            <div className="group absolute top-0 left-0 w-full rounded-[15px] h-full bg-black bg-opacity-0 hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                              <CameraAltIcon
                                style={{ fontSize: 30 }}
                                className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              />
                            </div>

                            <input
                              type="file"
                              accept="image/png, image/jpeg"
                              style={{ display: "none" }}
                              ref={(el) => (fileInputRefs.current[j] = el)}
                              onChange={(e) => handleIconUpload(index, j, e)}
                            />
                          </div>

                          <TextField
                            size="small"
                            label="Title"
                            sx={{ width: "90%" }}
                            value={i.title}
                            onChange={(e) => handleInputChange(index, j, e.target.value, "title")}
                          />
                          <TextField
                            size="small"
                            label="Button Text"
                            sx={{ width: "90%" }}
                            value={i.btnT}
                            onChange={(e) => handleInputChange(index, j, e.target.value, "btnT")}
                          />
                          <TextField
                            size="small"
                            label="Button Link"
                            sx={{ width: "90%" }}
                            value={i.btnL}
                            onChange={(e) => handleInputChange(index, j, e.target.value, "btnL")}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="relative pb-10">
                      {dataTable[index]?.sections?.map((item, ind) => (
                        <div
                          key={ind}
                          className="flex-col mt-3 relative"
                          style={{
                            alignItems: "center",
                            borderTop: "solid 1px #ddd",
                          }}>
                          <div className="flex-col gap-3 pt-5 w-7/12 self-start relative">
                            <TextField
                              size="small"
                              label="Section Title"
                              sx={{ width: "90%" }}
                              value={item.title}
                              onChange={(e) =>
                                handleSectionInputChange(index, ind, e.target.value, "title")
                              }
                            />
                            <TextField
                              size="small"
                              label="Section Description"
                              multiline
                              rows={3}
                              sx={{ width: "90%" }}
                              value={item.desc}
                              onChange={(e) =>
                                handleSectionInputChange(index, ind, e.target.value, "desc")
                              }
                            />
                          </div>
                          <div className="relative pb-14 mt-2 pr-2">
                            {dataTable[index]?.sections[ind]?.rows?.map((row, j) => (
                              <div className="flex mt-3" key={j}>
                                {[...Array(dataTable[index]?.header?.length + 1)].map(
                                  (_, colIndex) => (
                                    <div className="flex-col gap-5 border-l" key={colIndex}>
                                      <TextField
                                        inputProps={{
                                          style: {
                                            textAlign: "center",
                                          },
                                        }}
                                        size="small"
                                        label={colIndex === 0 ? "Name" : `Value ${colIndex}`}
                                        sx={{ width: "90%" }}
                                        value={colIndex === 0 ? row.n : row[`v${colIndex}`]}
                                        onChange={(e) =>
                                          handleRowInputChange(
                                            index,
                                            ind,
                                            j,
                                            e.target.value,
                                            colIndex === 0 ? "n" : `v${colIndex}`
                                          )
                                        }
                                      />
                                    </div>
                                  )
                                )}

                                {dataTable[index]?.sections[ind]?.rows?.length > 1 && (
                                  <IconButton size="small" onClick={() => removeRow(index, ind, j)}>
                                    <ClearIcon sx={{ color: "#d70b0b" }} />
                                  </IconButton>
                                )}
                              </div>
                            ))}

                            <div
                              onClick={() => addRow(index, ind)}
                              className="absolute bottom-0 hover:bg-[#25aceb40] w-full flex h-[35px] cursor-pointer transition-all delay-100 gap-3">
                              <AddCircleOutlineIcon sx={{ color: "var(--link)" }} />
                              <p style={{ color: "var(--link)" }}>Add Row</p>
                            </div>
                          </div>
                          <IconButton
                            style={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                            }}
                            onClick={() => removeSection(index, ind)}>
                            <ClearIcon sx={{ color: "#d70b0b", fontSize: 33 }} />
                          </IconButton>
                        </div>
                      ))}
                    </div>

                    <div
                      onClick={() => addSection(index)}
                      className="absolute bottom-0 hover:bg-[#25aceb40] w-full flex h-[35px] cursor-pointer transition-all delay-100 gap-3">
                      <AddCircleOutlineIcon sx={{ color: "var(--link)" }} />
                      <p style={{ color: "var(--link)" }}>Add Section</p>
                    </div>

                    <div
                      className="absolute right-0 top-2 flex gap-3 p-2"
                      onClick={() => addColumn(index)}>
                      <IconButton>
                        <AddCircleOutlineIcon sx={{ color: "var(--link)" }} />
                      </IconButton>
                    </div>
                  </div>

                  {user.perms[13].edit && (
                    <div className="submitBTN" onClick={submitData}>
                      Save Changes
                    </div>
                  )}
                </TabPanel>
              );
            })}
          </>
        )}
      </Box>
    </TabContext>
  );
}
